import type { MouseEventHandler, PropsWithChildren, ReactNode } from "react";

import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { Button } from "@/design-system/Button";
import { useTranslation } from "@/translations";

/**
 * Controls the confirm action's visual emphasis.
 *
 * - `primary`: Safe, expected actions such as save, retry, continue, or discard changes.
 * - `warning`: Consequential actions that need caution but are reversible or do not delete data.
 * - `error`: Destructive actions that delete data or cannot be undone.
 * - `success`: Positive acknowledgement after an action has already completed.
 */
export type ConfirmModalTone = "primary" | "error" | "warning" | "success";

export type ConfirmModalProps = ModalBaseProps & {
  title: string;
  description?: string;
  renderTitle?: (title: string) => ReactNode;
  renderDescription?: (title: string) => ReactNode;
  isLoading: boolean;
  onResolve: MouseEventHandler;
  resolveBtnProps?: {
    disabled?: boolean;
    hide?: boolean;
    text?: string;
    "data-testid"?: string;
  };
  onReject: MouseEventHandler;
  rejectBtnProps?: {
    hide?: boolean;
    text?: string;
    "data-testid"?: string;
  };
  tone: ConfirmModalTone;
  "data-testid"?: string;
  shouldCloseOnEsc?: boolean;
  isActionRequired?: boolean;
  onAfterClose?: () => void;
  shouldFocusContentOnOpen?: boolean;
};

export function ConfirmModal({
  title,
  description,
  isLoading,
  renderTitle = (x) => x,
  renderDescription = (x) => x,
  onReject,
  rejectBtnProps,
  onResolve,
  resolveBtnProps,
  children,
  isOpened,
  isActionRequired,
  tone,
  onAfterClose,
  onOpenChange,
  shouldFocusContentOnOpen,
  ...props
}: PropsWithChildren<ConfirmModalProps>): React.ReactNode {
  const { t } = useTranslation();

  return (
    <Modal.Root
      showClose={false}
      shouldCloseOnClickOutside={false}
      shouldCloseOnEsc
      data-testid={props["data-testid"]}
      isOpened={isOpened}
      onOpenChange={(state) => {
        if (!state) {
          onAfterClose?.();
        }

        onOpenChange(state);
      }}
      size="sm"
      title={renderTitle(title)}
      description={description && renderDescription(description)}
      descriptionTone="primary"
      isNested
      shouldFocusContentOnOpen={shouldFocusContentOnOpen}
    >
      {children && (
        <Modal.Body>
          <article className="relative grid grid-cols-1 gap-y-2">{children}</article>
        </Modal.Body>
      )}
      <Modal.Actions>
        {!rejectBtnProps?.hide && (
          <Button
            onClick={onReject}
            variant="outline"
            tone="primary"
            disabled={isLoading}
            data-testid={rejectBtnProps?.["data-testid"] || "confirm-modal-reject"}
          >
            {rejectBtnProps?.text ?? t("common.action.cancel")}
          </Button>
        )}
        {!resolveBtnProps?.hide && (
          <Button
            onClick={onResolve}
            disabled={resolveBtnProps?.disabled}
            isLoading={isLoading}
            variant="filled"
            tone={tone}
            data-testid={resolveBtnProps?.["data-testid"] || "confirm-modal-resolve"}
          >
            {resolveBtnProps?.text ?? t("common.action.ok")}
          </Button>
        )}
      </Modal.Actions>
    </Modal.Root>
  );
}
